import type {DeployEnv} from '@root/common/DeployEnv';

type NodeEnv = `development` | `production` | `test`;

const VALID_DEPLOY_ENVS: ReadonlySet<DeployEnv> = new Set([`local`, `localdev`, `localnative`, `uat`, `prod`]);
const VALID_NODE_ENVS: ReadonlySet<NodeEnv> = new Set([`development`, `production`, `test`]);

const isDeployEnv = (value: unknown): value is DeployEnv => typeof value === `string` && VALID_DEPLOY_ENVS.has(value as DeployEnv);
const isNodeEnv = (value: unknown): value is NodeEnv => typeof value === `string` && VALID_NODE_ENVS.has(value as NodeEnv);

type GatsbyConfig = {
  environment: DeployEnv;
  nodeEnv: NodeEnv;
};

let cachedConfig: GatsbyConfig | null = null;

const readGatsbyEnvironment = (): DeployEnv => {
  // eslint-disable-next-line no-process-env
  const rawValue = process.env.GATSBY_ENVIRONMENT;
  if (isDeployEnv(rawValue)) {
    return rawValue;
  }

  if (rawValue === undefined) {
    throw new Error(`GATSBY_ENVIRONMENT must be defined for microsite builds.`);
  }

  throw new Error(`Invalid value "${rawValue}" received for GATSBY_ENVIRONMENT. Expected one of: ${Array.from(VALID_DEPLOY_ENVS).join(`, `)}`);
};

const readNodeEnv = (): NodeEnv => {
  // eslint-disable-next-line no-process-env
  const value = process.env.NODE_ENV;
  if (isNodeEnv(value)) {
    return value;
  }

  if (value !== undefined) {
    console.warn(`Invalid value "${value}" received for NODE_ENV. Falling back to "production".`);
  }

  return `production`;
};

function createConfig(): GatsbyConfig {
  const environment = readGatsbyEnvironment();
  const nodeEnv = readNodeEnv();

  return {environment, nodeEnv};
}

export function getGatsbyConfig(): GatsbyConfig {
  if (cachedConfig === null) {
    cachedConfig = createConfig();
  }

  return cachedConfig;
}

export const getGatsbyEnvironment = () => getGatsbyConfig().environment;
